import getLoyaltyPointBurnCalculationKey from 'checkout/lib/utils/loyalty/getLoyaltyPointBurnCalculationKey'
import { EmptyArray, sortBy, sum } from 'lib/array/arrayUtils'
import { createSelector } from 'reselect'
import getAllItemsDiscountTotals from '../payment/getAllItemsDiscountTotals'
import getCreditPayAmount from '../payment/getCreditPayAmount'
import getPartnershipModifierAmount from '../payment/getPartnershipModifierAmount'
import getAllItemViews from '../view/getAllItemViews'
import getCheckoutLoyaltyItemsPriceInCurrency from './getCheckoutLoyaltyItemsPriceInCurrency'
import getCheckoutLoyaltyPointsBurnCalculationRequests from './getCheckoutLoyaltyPointsBurnCalculationRequests'

const LOADING_VALUE: App.StatefulData<App.Checkout.LoyaltyPointBurnCalculationView> = {
  fetching: true,
}
const EMPTY_VALUE: App.StatefulData<App.Checkout.LoyaltyPointBurnCalculationView> = {
  fetching: false,
  data: {
    burnableItems: EmptyArray,
    nonBurnableItems: EmptyArray,
    itemsTotalPriceInPoints: 0,
    maxBurnablePoints: 0,
    minBurnablePoints: 0,
  },
}

/**
 * View items are sorted by the highest point-to-currency ratio in desc order.
 */
const getCheckoutLoyaltyPointBurnCalculationView = createSelector(
  (state: App.State) => state.loyalty.account,
  (state: App.State) => getCheckoutLoyaltyPointsBurnCalculationRequests(state),
  (state: App.State) => state.loyalty.pointsBurnCalculations,
  (state: App.State) => getAllItemViews(state),
  (state: App.State) => getCheckoutLoyaltyItemsPriceInCurrency(state),
  (state: App.State) => getAllItemsDiscountTotals(state),
  (state: App.State) => getCreditPayAmount(state),
  (state: App.State) => getPartnershipModifierAmount(state),
  (
    loyaltyAccount,
    pointsBurnCalculationRequests,
    pointsBurnCalculations,
    allItemViews,
    itemsPriceInCurrency,
    allItemsDiscountTotals,
    creditPaymentAmount,
    partnershipModifierAmount,
  ): App.StatefulData<App.Checkout.LoyaltyPointBurnCalculationView> => {
    if (loyaltyAccount.initial || loyaltyAccount.fetching) return LOADING_VALUE
    if (!loyaltyAccount.data) return EMPTY_VALUE
    if (!allItemViews.hasRequiredData) return LOADING_VALUE
    if (!pointsBurnCalculationRequests.length) return EMPTY_VALUE

    const calculationKey = getLoyaltyPointBurnCalculationKey(pointsBurnCalculationRequests)
    const pointsBurnCalculation = pointsBurnCalculations[calculationKey]
    if (!pointsBurnCalculation?.data) return LOADING_VALUE

    let burnableItems: Array<App.Checkout.LoyaltyPointBurnCalculationViewBurnableItem> = []
    const nonBurnableItems: Array<App.Checkout.LoyaltyPointBurnCalculationViewBurnableItem> = []
    pointsBurnCalculation.data.items.forEach((calcItem) => {
      const itemView = allItemViews.data.get(calcItem.itemId)?.data
      if (itemView) {
        const actualPriceInCurrency = calcItem.priceInCurrency
        const actualPriceInPoints = calcItem.priceInPoints ?? 0
        let payablePriceInCurrency = actualPriceInCurrency
        let payablePriceInPoints = actualPriceInPoints
        const pricing = itemsPriceInCurrency.get(itemView.item.itemId)
        const loyaltyBenefitDiscount = pricing?.loyaltyUpgradeTargetPrice ? (pricing.loyaltyUpgradeTargetPrice - pricing.price) : 0
        const promoDiscount = allItemsDiscountTotals.get(itemView.item.itemId) ?? 0
        const itemDiscount = promoDiscount + loyaltyBenefitDiscount
        if (itemDiscount) {
          payablePriceInCurrency -= itemDiscount
          const burnRatio = actualPriceInPoints / actualPriceInCurrency
          payablePriceInPoints = Math.round(payablePriceInCurrency * burnRatio)
        }

        const item: App.Checkout.LoyaltyPointBurnCalculationViewBurnableItem = {
          itemView,
          payablePriceInCurrency,
          actualPriceInCurrency,
          payablePriceInPoints,
          actualPriceInPoints,
          title: itemView.title,
          appliedCredits: 0,
          promoDiscount,
          loyaltyBenefitDiscount,
        }
        if (item.payablePriceInPoints) burnableItems.push(item)
        else nonBurnableItems.push(item)
      }
    }, [])

    const creditsApplied = creditPaymentAmount + partnershipModifierAmount

    if (creditsApplied) {
      // sort from worst to best burn ratio to start applying credits to the worst ones first
      const preCreditsBurnableItems = sortBy(
        burnableItems,
        (i) => i.actualPriceInPoints / i.actualPriceInCurrency,
        'desc',
      )
      const adjustedBurnableItems: Array<App.Checkout.LoyaltyPointBurnCalculationViewBurnableItem> = []
      let creditsToApply = creditsApplied
      for (const preCreditBurnableItem of preCreditsBurnableItems) {
        if (creditsToApply <= 0) {
          adjustedBurnableItems.push(preCreditBurnableItem)
          continue
        }
        const creditsAppliedToItemPrice = Math.min(
          creditsToApply,
          preCreditBurnableItem.payablePriceInCurrency,
        )

        if (creditsAppliedToItemPrice === preCreditBurnableItem.payablePriceInCurrency) {
          nonBurnableItems.push(preCreditBurnableItem)
        } else {
          const adjustedPriceInCurrency = preCreditBurnableItem.payablePriceInCurrency - creditsAppliedToItemPrice
          const adjustPriceInPoints = Math.round((adjustedPriceInCurrency * preCreditBurnableItem.payablePriceInPoints) / preCreditBurnableItem.payablePriceInCurrency)
          adjustedBurnableItems.push({
            ...preCreditBurnableItem,
            payablePriceInCurrency: adjustedPriceInCurrency,
            payablePriceInPoints: adjustPriceInPoints,
            appliedCredits: creditsAppliedToItemPrice,
          })
        }
        creditsToApply -= creditsAppliedToItemPrice
      }
      burnableItems = adjustedBurnableItems
    }

    const itemsTotalPriceInPoints = sum(burnableItems, (item) => item.payablePriceInPoints)

    const maxBurnablePoints = Math.round(Math.min(
      loyaltyAccount.data.points,
      itemsTotalPriceInPoints,
    ))

    return {
      fetching: false,
      data: {
        burnableItems: sortBy(
          burnableItems,
          // sort by point-to-currency ratio
          (i) => i.actualPriceInPoints / i.actualPriceInCurrency,
          'asc',
        ),
        nonBurnableItems,
        itemsTotalPriceInPoints,
        maxBurnablePoints,
        minBurnablePoints: pointsBurnCalculation.data.minBurnablePoints,
      },
    }
  },
)

export default getCheckoutLoyaltyPointBurnCalculationView
